import { customElement, scss } from '../../core/ids-decorators';
import IdsElement from '../../core/ids-element';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLabelStateMixin from '../../mixins/ids-label-state-mixin/ids-label-state-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { validMaxHeight, parseNumberWithUnits } from '../../utils/ids-dom-utils/ids-dom-utils';
import type IdsListBoxOption from './ids-list-box-option';
import './ids-list-box-option';
import styles from './ids-list-box.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import type IdsCheckbox from '../ids-checkbox/ids-checkbox';
import '../ids-checkbox/ids-checkbox';

export interface IdsListBoxColumn {
  id: string;
  width: string;
}

const Base = IdsLabelStateMixin(
  IdsLabelPositionMixin(
    IdsKeyboardMixin(
      IdsEventsMixin(
        IdsElement
      )
    )
  )
);

/**
 * IDS List Box Component
 * @type {IdsListBox}
 * @inherits IdsElement
 * @mixes IdsLabelStateMixin
 * @mixes IdsLabelPositionMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsEventsMixin
 * @part container - the wrapper of the list box
 * @part label - the label element
 * @part field-container - the container of the options
 */
@customElement('ids-list-box')
@scss(styles)
export default class IdsListBox extends Base {
  columnsData: IdsListBoxColumn[] = [];

  #data: Record<string, unknown>[] = [];

  constructor() {
    super();
  }

  private cachedIsEmbedded: boolean | null = null;

  #slotChangeHandler: (() => void) | null = null;

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.HEIGHT,
      attributes.ITEM_HEIGHT,
      attributes.MAX_HEIGHT,
      attributes.MAX_WIDTH,
      attributes.MIN_HEIGHT,
      attributes.MIN_WIDTH,
      attributes.WIDTH,
      attributes.MULTIPLE,
      attributes.MULTI_COLUMN,
      attributes.SHOW_CHECKBOXES,
    ];
  }

  /**
   * Lifecycle hook called when element is inserted into the DOM
   * Initializes the listbox role and configures options
   */
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(htmlAttributes.ROLE, 'listbox');
    this.#configureListBoxOptions();
    this.#applySizingStyles();
    this.#syncAriaLabel();
    this.onLabelWrapChange();
    this.#attachSlotChangeListener();
  }

  /**
   * Lifecycle hook called after element is mounted and DOM is ready
   * Initializes embedded-specific behavior and keyboard handling
   */
  mountedCallback() {
    if (!this.isEmbedded) this.setAttribute(htmlAttributes.ARIA_MULTISELECTABLE, 'true');
    this.#updateClasses();
    this.#initializeNonEmbeddedBehavior();
    this.#attachLabelClickListener();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot && this.#slotChangeHandler) {
      slot.removeEventListener('slotchange', this.#slotChangeHandler);
      this.#slotChangeHandler = null;
    }
  }

  /**
   * Create the Template for the contents.
   * The label-state mixin will populate the label content via setLabelText after attributes
   * are applied. The `empty` class is removed by the mixin once a label value is present.
   * @returns {string} The template
   */
  template(): string {
    return `<div class="ids-list-box" part="container">
      <label class="ids-label-text empty no-required-indicator" part="label">
        <ids-text part="label" color-unset></ids-text>
      </label>
      <div class="field-container" part="field-container">
        <slot></slot>
      </div>
    </div>`;
  }

  /**
   * Override the label container used by IdsLabelPositionMixin
   * @returns {HTMLElement | null} The container element used for layout adjustments
   */
  labelContainer(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.ids-list-box') || null;
  }

  /**
   * Get list of options
   * @returns {IdsListBoxOption[]} ids-list-box-option child elements
   */
  get options(): IdsListBoxOption[] {
    return [...this.querySelectorAll<IdsListBoxOption>('ids-list-box-option')];
  }

  /**
   * Check if this list-box is embedded in a dropdown/multiselect
   * @returns {boolean} True if embedded, false otherwise
   */
  get isEmbedded(): boolean {
    // Cache the result since parent doesn't change after connectedCallback
    if (this.cachedIsEmbedded === null) {
      const parent = this.parentElement;
      this.cachedIsEmbedded = parent?.tagName === 'IDS-DROPDOWN'
        || parent?.tagName === 'IDS-DROPDOWN-LIST'
        || parent?.tagName === 'IDS-MULTISELECT'
        || (parent?.tagName === 'IDS-POPUP' && parent?.getAttribute('type') === 'dropdown') || false;
    }
    return this.cachedIsEmbedded;
  }

  /**
   * Get the multiple selection mode
   * @returns {boolean} True if multiple selection is enabled
   */
  get multiple(): boolean {
    // Always true when not embedded, can be disabled with false attribute
    if (!this.isEmbedded) {
      return !this.hasAttribute(attributes.MULTIPLE) || this.getAttribute(attributes.MULTIPLE) !== 'false';
    }
    return false;
  }

  /**
   * Set the multiple selection mode
   * @param {boolean | string} value The value
   */
  set multiple(value: boolean | string) {
    if (stringToBool(value)) {
      this.removeAttribute(attributes.MULTIPLE);
    } else {
      this.setAttribute(attributes.MULTIPLE, 'false');
    }
  }

  /**
   * Get all selected options
   * @returns {IdsListBoxOption[]} Array of selected options
   */
  get selectedOptions(): IdsListBoxOption[] {
    return this.options.filter((opt) => opt.selected);
  }

  /**
   * Get whether multi-column mode is enabled
   * @returns {boolean} true if multi-column is enabled
   */
  get multiColumn(): boolean {
    return this.hasAttribute(attributes.MULTI_COLUMN);
  }

  /**
   * Set whether multi-column mode is enabled
   * @param {boolean} value If true will set `multi-column` attribute
   */
  set multiColumn(value: boolean) {
    this.toggleAttribute(attributes.MULTI_COLUMN, stringToBool(value));
    this.classList.toggle('multi-column', stringToBool(value));
    this.classList.toggle('is-table', stringToBool(value));
    this.#configureListBoxOptions();
  }

  /**
   * Get whether checkboxes should be shown in multiselect mode
   * @returns {boolean} true if checkboxes are shown
   */
  get showCheckboxes(): boolean {
    return this.hasAttribute(attributes.SHOW_CHECKBOXES);
  }

  /**
   * Set whether checkboxes should be shown in multiselect mode
   * @param {boolean} value If true will set `show-checkboxes` attribute
   */
  set showCheckboxes(value: boolean) {
    this.toggleAttribute(attributes.SHOW_CHECKBOXES, stringToBool(value));

    // Add or remove checkboxes from existing options
    if (stringToBool(value)) {
      this.options.forEach((option: IdsListBoxOption) => {
        if (!option.querySelector('ids-checkbox')) {
          const label = option.textContent || option.getAttribute('value') || '';
          const checkbox = this.#createOptionCheckbox(label, option.selected);
          option.insertBefore(checkbox, option.firstChild);
        }
      });
    } else {
      this.options.forEach((option: IdsListBoxOption) => {
        const checkbox = option.querySelector('ids-checkbox');
        if (checkbox) {
          checkbox.remove();
        }
      });
    }

    this.#configureListBoxOptions();
  }

  /**
   * Get the columns configuration for multi-column layout
   * @returns {IdsListBoxColumn[]} the columns configuration
   */
  get columns(): IdsListBoxColumn[] {
    return this.columnsData || [];
  }

  /**
   * Set the columns configuration for multi-column layout
   * @param {IdsListBoxColumn[]} val Array of column configuration objects
   */
  set columns(val: IdsListBoxColumn[]) {
    if (Array.isArray(val)) {
      this.columnsData = val;
      this.#applyColumnWidths();
    }
  }

  /**
   * Get the data array for populating options
   * @returns {Record<string, unknown>[]} The data array
   */
  get data(): Record<string, unknown>[] {
    return this.#data;
  }

  /**
   * Set the data array and populate options from it
   * Creates ids-list-box-option elements with spans for each column
   * @param {Record<string, unknown>[]} val Array of data objects
   */
  set data(val: Record<string, unknown>[]) {
    if (!Array.isArray(val)) return;

    this.#data = val;

    // Clear existing options
    this.options.forEach((opt) => opt.remove());

    // Create options from data
    val.forEach((item: any) => {
      const option = document.createElement('ids-list-box-option') as any;

      // Set value and id from data
      if (item.value) option.value = item.value;
      if (item.id) option.id = item.id;
      if (item.tooltip) option.setAttribute(attributes.TOOLTIP, item.tooltip);

      // Add checkbox if show-checkboxes is enabled
      if (this.hasAttribute(attributes.SHOW_CHECKBOXES)) {
        const label = item.label || item.value || '';
        const checkbox = this.#createOptionCheckbox(label, false);
        option.appendChild(checkbox);
      }

      if (this.columnsData && this.columnsData.length > 0) {
        this.columnsData.forEach((column: IdsListBoxColumn) => {
          const span = document.createElement('span');
          span.textContent = item[column.id] || '';
          option.appendChild(span);
        });
      } else {
        option.textContent = item.label || item.value || '';
      }

      // Set selected state after all content is added
      if (item.selected) {
        option.selected = true;
      }

      this.appendChild(option);
    });

    // Reconfigure options after adding them
    this.#configureListBoxOptions();
    this.#applyColumnWidths();
  }

  /**
   * Get sorted list of options by row index
   * @returns {IdsListBoxOption[]} Sorted ids-list-box-option child elements
   */
  get optionsSorted(): IdsListBoxOption[] {
    return this.options.sort(
      (current: IdsListBoxOption, next: IdsListBoxOption) => (current.rowIndex >= next.rowIndex ? 1 : -1)
    );
  }

  /**
   * @returns {string | null} The max height value
   */
  get maxHeight(): string | null {
    return this.getAttribute(attributes.MAX_HEIGHT);
  }

  /**
   * Set the max height value
   * @param {string | number | null} value The value
   */
  set maxHeight(value: string | number | null) {
    const val = validMaxHeight(value);
    if (val) {
      this.setAttribute(attributes.MAX_HEIGHT, val);
      this.style.setProperty('--ids-list-box-max-height', val);
    } else {
      this.removeAttribute(attributes.MAX_HEIGHT);
      this.style.removeProperty('--ids-list-box-max-height');
    }
  }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value string value from the disabled attribute
   */
  set disabled(value: boolean | string) {
    this.toggleAttribute(attributes.DISABLED, stringToBool(value));
  }

  /**
   * Gets the disabled attribute
   * @returns {boolean | string} The disabled state
   */
  get disabled(): boolean | string {
    return this.hasAttribute(attributes.DISABLED);
  }

  /**
   * @returns {string | null} The width value
   */
  get width(): string | null {
    return this.getAttribute(attributes.WIDTH);
  }

  /**
   * Set the width value
   * @param {string | number | null} value The value
   */
  set width(value: string | number | null) {
    const val = parseNumberWithUnits(value);
    if (val) {
      this.setAttribute(attributes.WIDTH, String(value));
      this.style.width = val;
    } else {
      this.removeAttribute(attributes.WIDTH);
      this.style.width = '';
    }
  }

  /**
   * @returns {string | null} The min-width value
   */
  get minWidth(): string | null {
    return this.getAttribute(attributes.MIN_WIDTH);
  }

  /**
   * Set the min-width value
   * @param {string | number | null} value The value
   */
  set minWidth(value: string | number | null) {
    const val = parseNumberWithUnits(value);
    if (val) {
      this.setAttribute(attributes.MIN_WIDTH, String(value));
      this.style.minWidth = val;
    } else {
      this.removeAttribute(attributes.MIN_WIDTH);
      this.style.minWidth = '';
    }
  }

  /**
   * @returns {string | null} The max-width value
   */
  get maxWidth(): string | null {
    return this.getAttribute(attributes.MAX_WIDTH);
  }

  /**
   * Set the max-width value
   * @param {string | number | null} value The value
   */
  set maxWidth(value: string | number | null) {
    const val = parseNumberWithUnits(value);
    if (val) {
      this.setAttribute(attributes.MAX_WIDTH, String(value));
      this.style.maxWidth = val;
    } else {
      this.removeAttribute(attributes.MAX_WIDTH);
      this.style.maxWidth = '';
    }
  }

  /**
   * @returns {string | null} The height value
   */
  get height(): string | null {
    return this.getAttribute(attributes.HEIGHT);
  }

  /**
   * Set the height value
   * @param {string | number | null} value The value
   */
  set height(value: string | number | null) {
    const val = parseNumberWithUnits(value);
    if (val) {
      this.setAttribute(attributes.HEIGHT, String(value));
      this.style.setProperty('--ids-list-box-height', val);
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.style.removeProperty('--ids-list-box-height');
    }
  }

  /**
   * @returns {string | null} The min-height value
   */
  get minHeight(): string | null {
    return this.getAttribute(attributes.MIN_HEIGHT);
  }

  /**
   * Set the min-height value
   * @param {string | number | null} value The value
   */
  set minHeight(value: string | number | null) {
    const val = parseNumberWithUnits(value);
    if (val) {
      this.setAttribute(attributes.MIN_HEIGHT, String(value));
      this.style.minHeight = val;
    } else {
      this.removeAttribute(attributes.MIN_HEIGHT);
      this.style.minHeight = '';
    }
  }

  /**
   * @returns {string | null} The item-height value
   */
  get itemHeight(): string | null {
    return this.getAttribute(attributes.ITEM_HEIGHT);
  }

  /**
   * Set the item-height value and apply it as a CSS variable for option height
   * @param {string | number | null} value The value
   */
  set itemHeight(value: string | number | null) {
    const val = parseNumberWithUnits(value);
    if (val) {
      this.setAttribute(attributes.ITEM_HEIGHT, String(value));
      this.style.setProperty('--ids-list-box-option-height', val);
    } else {
      this.removeAttribute(attributes.ITEM_HEIGHT);
      this.style.removeProperty('--ids-list-box-option-height');
    }
  }

  /**
   * Callback fired when label-state changes - keeps aria-label on host in sync
   * @returns {void}
   */
  onLabelStateChange(): void {
    this.#syncAriaLabel();
  }

  /**
   * Callback fired when label-wrap changes - update inner ids-text element
   * @returns {void}
   */
  onLabelWrapChange(): void {
    const wrap = this.getAttribute(attributes.LABEL_WRAP);
    const idsText = this.shadowRoot?.querySelector('label ids-text');
    if (!idsText) return;

    if (wrap) {
      idsText.setAttribute(attributes.LABEL_WRAP, wrap);
    } else {
      idsText.removeAttribute(attributes.LABEL_WRAP);
    }

    const hasEllipsis = wrap === 'ellipsis' || wrap === 'ellipsis-no-stretch';
    if (hasEllipsis) {
      idsText.setAttribute(attributes.OVERFLOW, 'ellipsis');
      if (this.label) idsText.setAttribute(attributes.TOOLTIP, this.label);
    } else {
      idsText.removeAttribute(attributes.OVERFLOW);
      idsText.removeAttribute(attributes.TOOLTIP);
    }

    // For ellipsis-no-stretch and wrap-no-stretch, remove the max-inline-size constraint on the inner span
    if (wrap === 'ellipsis-no-stretch' || wrap === 'wrap-no-stretch') {
      const span = idsText.shadowRoot?.querySelector('span');
      if (span) {
        span.style.maxInlineSize = 'calc(100% - 4px)';
      }
    }
  }

  /**
   * Create an ids-checkbox element for a list box option
   * @private
   * @param {string} label The label text for the checkbox
   * @param {boolean} [checked] Whether the checkbox should be initially checked
   * @returns {HTMLElement} The created checkbox element
   */
  #createOptionCheckbox(label: string, checked?: boolean): HTMLElement {
    const checkbox = document.createElement('ids-checkbox') as IdsCheckbox;
    const selectText = (this as any).localeAPI?.translate('Select') || 'Select';
    if (label) {
      checkbox.setAttribute(attributes.LABEL, `${selectText} ${label.trim()}`);
      checkbox.setAttribute(attributes.LABEL_STATE, 'hidden');
      checkbox.setAttribute(attributes.LABEL_WIDTH, '0');
    }
    checkbox.setAttribute(attributes.INERT, '');
    if (checked) checkbox.checked = true;
    return checkbox;
  }

  /**
   * Sync the aria-label attribute on the host element with the visible label.
   * When the label is hidden/collapsed, screen readers still need a name.
   * @private
   */
  #syncAriaLabel(): void {
    const label = this.label;
    if (label) {
      this.setAttribute(htmlAttributes.ARIA_LABEL, label);
    } else {
      this.removeAttribute(htmlAttributes.ARIA_LABEL);
    }
  }

  /**
   * Configure list box options with appropriate classes and properties
   * Handles multi-column layout, overflow ellipsis, and row indexing
   * @private
   */
  #configureListBoxOptions(): void {
    let isMultiColumn = false;
    const isOverflow = this.parentElement?.hasAttribute(attributes.OVERFLOW);
    const showCheckboxes = this.hasAttribute(attributes.SHOW_CHECKBOXES) && this.multiple && !this.isEmbedded;

    if (this.parentElement?.hasAttribute(attributes.MULTI_COLUMN)) {
      isMultiColumn = stringToBool(this.parentElement?.getAttribute(attributes.MULTI_COLUMN));
    }
    if (this.hasAttribute(attributes.MULTI_COLUMN)) {
      isMultiColumn = stringToBool(this.getAttribute(attributes.MULTI_COLUMN));
    }

    this.options.forEach((option: IdsListBoxOption, index) => {
      if (!option.id) {
        option.id = `${this.id || 'ids-list-box'}-option-${index}`;
      }

      if (isOverflow) option.classList.add('ellipsis');
      if (isMultiColumn) {
        option.classList.add('multi-column-option');
        const slotEl = option.shadowRoot?.querySelector('slot');
        if (slotEl) {
          slotEl.assignedElements().forEach((object) => { object.classList.add('multi-key'); });
        }
      }
      if (showCheckboxes) {
        option.classList.add('show-checkboxes');
      } else {
        option.classList.remove('show-checkboxes');
      }
      option.rowIndex = index;
      option.setAttribute(htmlAttributes.ARIA_SELECTED, option.selected ? 'true' : 'false');
    });
  }

  /**
   * Apply sizing styles from width/height attributes to inline styles
   * Called during connectedCallback to ensure dimensions are applied
   * @private
   */
  #applySizingStyles(): void {
    if (this.isEmbedded) return;
    if (this.width) this.style.width = parseNumberWithUnits(this.width) || '';
    if (this.minWidth) this.style.minWidth = parseNumberWithUnits(this.minWidth) || '';
    if (this.maxWidth) this.style.maxWidth = parseNumberWithUnits(this.maxWidth) || '';
    if (this.height) {
      const heightVal = parseNumberWithUnits(this.height) || '';
      this.style.setProperty('--ids-list-box-height', heightVal);
    }
    if (this.minHeight) this.style.minHeight = parseNumberWithUnits(this.minHeight) || '';
    if (this.maxHeight) this.style.setProperty('--ids-list-box-max-height', this.maxHeight);
    if (this.itemHeight) this.style.setProperty('--ids-list-box-option-height', parseNumberWithUnits(this.itemHeight) || '');
  }

  /**
   * Apply column widths to spans in multi-column options
   * @private
   */
  #applyColumnWidths(): void {
    if (!this.columnsData || this.columnsData.length === 0) return;

    this.options.forEach((option: IdsListBoxOption) => {
      const spans = option.querySelectorAll('span');
      spans.forEach((span: HTMLSpanElement, index: number) => {
        if (this.columnsData[index]) {
          span.style.width = this.columnsData[index].width;
        }
      });
    });
  }

  /**
   * Update all component classes based on state
   * Handles embedded state, multi-column layout, and table styling
   * @private
   */
  #updateClasses(): void {
    this.classList.toggle('not-embedded', !this.isEmbedded);
    this.classList.toggle('multi-column', this.multiColumn);
    this.classList.toggle('is-table', this.multiColumn);
  }

  /**
   * Initialize keyboard and focus handling for non-embedded list boxes
   * @private
   */
  #initializeNonEmbeddedBehavior(): void {
    if (this.isEmbedded) return;
    this.#setInitialFocus();
    this.#attachKeyboardListeners();
  }

  /**
   * Attach slotchange listener to react when child options are added after connectedCallback.
   * This ensures show-checkboxes and CSS classes are applied when options are appended
   * programmatically (e.g. via ExtJS afterrender or any late DOM insertion).
   * @private
   */
  #attachSlotChangeListener(): void {
    const slot = this.shadowRoot?.querySelector('slot');
    if (!slot) return;

    this.#slotChangeHandler = () => {
      if (!this.isEmbedded) {
        this.#configureListBoxOptions();
      }

      if (this.showCheckboxes && !this.isEmbedded) {
        this.options.forEach((option: IdsListBoxOption) => {
          if (!option.querySelector('ids-checkbox')) {
            const label = option.textContent || option.getAttribute('value') || '';
            const checkbox = this.#createOptionCheckbox(label, option.selected);
            option.insertBefore(checkbox, option.firstChild);
          }
        });
      }
    };

    slot.addEventListener('slotchange', this.#slotChangeHandler);
  }

  /**
   * Attach click listener to the label to focus the list box
   * @private
   */
  #attachLabelClickListener(): void {
    const label = this.shadowRoot?.querySelector<HTMLElement>('label');
    if (!label) return;

    label.addEventListener('click', () => {
      const focusedOption = this.#getFocusedOption();
      if (focusedOption) {
        focusedOption.focus();
      } else if (this.options[0]) {
        this.#setFocusedOption(this.options[0]);
      }
    });
  }

  /**
   * Set initial focus on the first option
   * @private
   */
  #setInitialFocus(): void {
    const firstOption = this.options[0];
    if (firstOption) {
      firstOption.setAttribute(htmlAttributes.TABINDEX, '0');
      // Set all other options to -1
      this.options.slice(1).forEach((opt) => {
        opt.setAttribute(htmlAttributes.TABINDEX, '-1');
      });
    }
  }

  /**
   * Attach keyboard event listeners for navigation
   * @private
   */
  #attachKeyboardListeners(): void {
    this.listen(['ArrowDown'], this, (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.#focusNextOption();
    });

    this.listen(['ArrowUp'], this, (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.#focusPreviousOption();
    });

    this.listen(['Home'], this, (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.#focusFirstOption();
    });

    this.listen(['End'], this, (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.#focusLastOption();
    });

    this.listen([' ', 'Enter'], this, (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const focusedOption = this.#getFocusedOption();
      if (focusedOption) {
        this.#selectOption(focusedOption);
      }
    });

    // Handle click to select and focus
    this.onEvent('click.list-box', this, (e: Event) => {
      const target = e.target as HTMLElement;
      const option = target.closest('ids-list-box-option') as IdsListBoxOption;
      if (option && this.options.includes(option)) {
        this.#selectOption(option);
        this.#setFocusedOption(option);
      }
    });
  }

  /**
   * Focus the next option in the list
   * @private
   */
  #focusNextOption(): void {
    const options = this.options;
    const focusedOption = this.#getFocusedOption();
    let nextIndex = 0;

    if (focusedOption) {
      const currentIndex = options.indexOf(focusedOption);
      nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : currentIndex;
    }

    if (options[nextIndex]) {
      this.#setFocusedOption(options[nextIndex]);
    }
  }

  /**
   * Focus the previous option in the list
   * @private
   */
  #focusPreviousOption(): void {
    const options = this.options;
    const focusedOption = this.#getFocusedOption();
    let prevIndex = 0;

    if (focusedOption) {
      const currentIndex = options.indexOf(focusedOption);
      prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
    }

    if (options[prevIndex]) {
      this.#setFocusedOption(options[prevIndex]);
    }
  }

  /**
   * Focus the first option in the list
   * @private
   */
  #focusFirstOption(): void {
    if (this.options[0]) {
      this.#setFocusedOption(this.options[0]);
    }
  }

  /**
   * Focus the last option in the list
   * @private
   */
  #focusLastOption(): void {
    const options = this.options;
    const lastOption = options[options.length - 1];
    if (lastOption) {
      this.#setFocusedOption(lastOption);
    }
  }

  /**
   * Get the currently focused option
   * @private
   * @returns {IdsListBoxOption | null} The currently focused option or null
   */
  #getFocusedOption(): IdsListBoxOption | null {
    return this.options.find((opt) => opt.getAttribute('tabindex') === '0') || null;
  }

  /**
   * Set focus on a specific option
   * @private
   * @param {IdsListBoxOption | undefined} option The option to focus
   */
  #setFocusedOption(option: IdsListBoxOption | undefined): void {
    if (!option) return;

    // Remove tabindex from all options
    this.options.forEach((opt) => {
      opt.setAttribute(htmlAttributes.TABINDEX, '-1');
    });

    // Set tabindex on the focused option and focus it
    option.setAttribute(htmlAttributes.TABINDEX, '0');
    option.focus();

    // Set aria-activedescendant to the focused option's id
    if (option.id) {
      this.setAttribute(htmlAttributes.ARIA_ACTIVE_DESCENDANT, option.id);
    }
  }

  /**
   * Select an option, handling single vs multiple selection
   * @private
   * @param {IdsListBoxOption} option The option to select
   */
  #selectOption(option: IdsListBoxOption): void {
    const wasSelected = option.selected;

    if (this.multiple) {
      option.selected = !option.selected;
    } else {
      this.options.forEach((opt) => {
        if (opt !== option && opt.selected) {
          opt.selected = false;
          opt.setAttribute(htmlAttributes.ARIA_SELECTED, 'false');
          this.triggerEvent('deselected', this, {
            bubbles: false,
            detail: {
              elem: this,
              label: opt.textContent,
              value: opt.value,
            }
          });
        }
      });
      option.selected = true;
    }

    option.setAttribute(htmlAttributes.ARIA_SELECTED, String(option.selected));

    if (option.selected !== wasSelected) {
      this.triggerEvent('change', this, { bubbles: false });

      if (option.selected) {
        this.triggerEvent('selected', this, {
          bubbles: false,
          detail: {
            elem: this,
            label: option.textContent,
            value: option.value,
          }
        });
      } else if (this.multiple) {
        this.triggerEvent('deselected', this, {
          bubbles: false,
          detail: {
            elem: this,
            label: option.textContent,
            value: option.value,
          }
        });
      }

      this.triggerEvent('selectionchanged', this, {
        bubbles: false,
        detail: {
          elem: this,
          selectedOptions: this.selectedOptions,
        }
      });
    }
  }
}
